import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { doc, updateDoc, collection, getDocs, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration, ProgramInterests } from '../../../types/registration';
import { AgeCategory, YouthChildrenSubCategory } from '../../../types/camper';
import { calculateNights, getNightsDisplayText } from './utils/registrationUtils';

interface AttendingCampersCardProps {
  registration: Registration;
  campSettings: any;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
}

const AttendingCampersCard: React.FC<AttendingCampersCardProps> = ({
  registration,
  campSettings,
  onRegistrationUpdate
}) => {
  const [isEditingCampers, setIsEditingCampers] = useState(false);
  const [editingCampers, setEditingCampers] = useState<any[]>([]);
  const [historicalCampers, setHistoricalCampers] = useState<any[]>([]);
  const [showAddNewCamper, setShowAddNewCamper] = useState(false);
  const [selectedHistoricalCamper, setSelectedHistoricalCamper] = useState('');
  const [newCamper, setNewCamper] = useState({ 
    firstName: '', 
    lastName: '', 
    age: AgeCategory.Adult, 
    youthChildrenCategory: undefined as YouthChildrenSubCategory | undefined,
    nights: 0 
  });

  const normalizeAgeCategory = (age: string): AgeCategory => {
    const normalizedAge = String(age || '')
      .trim()
      .replace(/\s+/g, '')
      .toLowerCase();

    if (normalizedAge === 'adult') return AgeCategory.Adult;
    if (normalizedAge === 'adultstudent') return AgeCategory.AdultStudent;
    if (normalizedAge === 'youthchildren' || normalizedAge === 'youth/children') return AgeCategory.YouthChildren;
    if (normalizedAge === 'mustchange') return AgeCategory.MustChange;
    if (normalizedAge === 'needsselection') return AgeCategory.NeedsSelection;

    return AgeCategory.MustChange;
  };

  const hasUnresolvedAgeCategory = (age: string): boolean => {
    const normalizedAge = normalizeAgeCategory(age);
    return normalizedAge === AgeCategory.MustChange || normalizedAge === AgeCategory.NeedsSelection;
  };

  const resolveYouthChildrenCategory = (camper: any): YouthChildrenSubCategory | undefined => {
    return camper?.YouthChildrenCategory || camper?.youthChildrenCategory || camper?.youthChildrenSubCategory;
  };

  const normalizeCamperForSave = (camper: any) => {
    const normalizedCamper = { ...camper };
    delete normalizedCamper.youthChildrenCategory;
    delete normalizedCamper.youthChildrenSubCategory;
    const normalizedAge = normalizeAgeCategory(camper.age);
    const resolvedCategory = resolveYouthChildrenCategory(camper);
    return {
      ...normalizedCamper,
      age: normalizedAge,
      YouthChildrenCategory: normalizedAge === AgeCategory.YouthChildren ? (resolvedCategory || null) : null
    };
  };

  const fetchHistoricalCampers = async () => {
    try {
      const historicalCampersSet = new Set<string>();
      const campersList: any[] = [];

      const currentlyAttendingKeys = new Set(
        (registration?.attendingCampers || []).map(
          (camper: any) => `${String(camper?.firstName || '').trim().toLowerCase()}_${String(camper?.lastName || '').trim().toLowerCase()}`
        )
      );

      const addCamperIfUnique = (camper: any) => {
        const firstName = String(camper?.firstName ?? camper?.FirstName ?? '').trim();
        const lastName = String(camper?.lastName ?? camper?.LastName ?? '').trim();
        if (!firstName || !lastName) return;

        const camperKey = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
        if (historicalCampersSet.has(camperKey) || currentlyAttendingKeys.has(camperKey)) return;

        historicalCampersSet.add(camperKey);
        campersList.push({
          id: camperKey,
          firstName,
          lastName,
          age: camper?.age ?? camper?.Age ?? AgeCategory.MustChange
        });
      };

      const primaryCamperId =
        (registration as any)?.registrationInfo?.primaryCamperId ||
        (registration as any)?.primaryCamperId;

      let primaryCamperData: any | null = null;

      if (primaryCamperId) {
        const primaryCamperSnap = await getDoc(doc(db, 'primaryCampers', primaryCamperId));
        if (primaryCamperSnap.exists()) {
          primaryCamperData = primaryCamperSnap.data();
        }
      }

      if (!primaryCamperData && registration?.registrationInfo?.email) {
        const primaryCampersQuery = query(
          collection(db, 'primaryCampers'),
          where('email', '==', registration.registrationInfo.email)
        );
        const primaryCampersSnapshot = await getDocs(primaryCampersQuery);
        if (!primaryCampersSnapshot.empty) {
          primaryCamperData = primaryCampersSnapshot.docs[0].data();
        }
      }

      if (primaryCamperData?.dependants && Array.isArray(primaryCamperData.dependants)) {
        primaryCamperData.dependants.forEach((dependant: any) => addCamperIfUnique(dependant));
      }
      
      setHistoricalCampers(campersList);
    } catch (error) {
      console.error('Error fetching historical campers:', error);
    }
  };

  const handleEditCampersClick = async () => {
    if (registration?.attendingCampers) {
      setEditingCampers([...registration.attendingCampers]);
    }
    await fetchHistoricalCampers();
    setIsEditingCampers(true);
  };

  const handleCancelEditCampers = () => {
    setIsEditingCampers(false);
    setEditingCampers([]);
    setNewCamper({ 
      firstName: '', 
      lastName: '', 
      age: AgeCategory.Adult, 
      youthChildrenCategory: undefined,
      nights: 0 
    });
    setHistoricalCampers([]);
    setShowAddNewCamper(false);
    setSelectedHistoricalCamper('');
  };

  const handleSaveCampers = async () => {
    if (!registration || !campSettings) return;
    
    try {
      const normalizedCampers = editingCampers.map(normalizeCamperForSave);
      const campersWithUnresolvedAge = normalizedCampers.filter((camper) => hasUnresolvedAgeCategory(camper.age));
      if (campersWithUnresolvedAge.length > 0) {
        alert('Please set a valid category for all campers. "Must Change" categories cannot be saved.');
        return;
      }
      const updatedRegistrationFees: any = {
        adults: [],
        adultStudents: [],
        youthChildren: [],
        totalRegistrationFees: 0
      };

      normalizedCampers.forEach(camper => {
        const normalizedAge = normalizeAgeCategory(camper.age);
        const nights = camper.nights || 0;
        const isFullCamp = nights >= 5;
        const camperName = `${camper.firstName} ${camper.lastName}`;
        
        if (normalizedAge === AgeCategory.Adult) {
          const fee = isFullCamp 
            ? campSettings.rates.registration.adult.weekly 
            : campSettings.rates.registration.adult.nightly * nights;
          updatedRegistrationFees.adults.push({
            name: camperName,
            fee: fee,
            nights: nights
          });
          updatedRegistrationFees.totalRegistrationFees += fee;
        } else if (normalizedAge === AgeCategory.AdultStudent) {
          const fee = isFullCamp 
            ? campSettings.rates.registration.adultStudent.weekly 
            : campSettings.rates.registration.adultStudent.nightly * nights;
          updatedRegistrationFees.adultStudents.push({
            name: camperName,
            fee: fee,
            nights: nights
          });
          updatedRegistrationFees.totalRegistrationFees += fee;
        } else if (normalizedAge === AgeCategory.YouthChildren) {
          const fee = isFullCamp 
            ? campSettings.rates.registration.youthAndChildren.weekly 
            : campSettings.rates.registration.youthAndChildren.nightly * nights;
          updatedRegistrationFees.youthChildren.push({
            name: camperName,
            fee: fee,
            nights: nights
          });
          updatedRegistrationFees.totalRegistrationFees += fee;
        }
      });

      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        attendingCampers: normalizedCampers,
        'financial.registrationFees': updatedRegistrationFees
      });

      const updatedRegistration = {
        ...registration,
        attendingCampers: normalizedCampers,
        financial: {
          ...registration.financial,
          registrationFees: updatedRegistrationFees
        }
      } as Registration;

      onRegistrationUpdate(updatedRegistration);
      setIsEditingCampers(false);
      setEditingCampers([]);
      setHistoricalCampers([]);
      setShowAddNewCamper(false);
      setNewCamper({ 
        firstName: '', 
        lastName: '', 
        age: AgeCategory.Adult, 
        youthChildrenCategory: undefined,
        nights: 0 
      });
    } catch (error) {
      console.error('Error updating campers:', error);
      alert('Failed to update campers. Please try again.');
    }
  };

  const handleCamperCategoryChange = async (camperIndex: number, age: AgeCategory) => {
    if (!registration?.attendingCampers) return;

    try {
      const updatedCampers: any[] = (registration.attendingCampers as any[]).map(normalizeCamperForSave);
      updatedCampers[camperIndex] = {
        ...updatedCampers[camperIndex],
        age,
        YouthChildrenCategory: age === AgeCategory.YouthChildren
          ? (resolveYouthChildrenCategory(updatedCampers[camperIndex]) || YouthChildrenSubCategory.Nursery)
          : null
      };

      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        attendingCampers: updatedCampers
      });

      onRegistrationUpdate({
        ...registration,
        attendingCampers: updatedCampers
      } as Registration);
    } catch (error) {
      console.error('Error updating camper category:', error);
      alert('Failed to update camper category. Please try again.');
    }
  };

  const handleYouthChildrenSubCategoryChange = async (camperIndex: number, youthChildrenCategory: YouthChildrenSubCategory) => {
    if (!registration?.attendingCampers) return;

    try {
      const updatedCampers = registration.attendingCampers.map(normalizeCamperForSave);
      updatedCampers[camperIndex] = {
        ...updatedCampers[camperIndex],
        YouthChildrenCategory: youthChildrenCategory
      };

      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        attendingCampers: updatedCampers
      });

      onRegistrationUpdate({
        ...registration,
        attendingCampers: updatedCampers
      } as Registration);
    } catch (error) {
      console.error('Error updating youth/children category:', error);
      alert('Failed to update youth/children category. Please try again.');
    }
  };

  const handleCamperNightsChange = (index: number, nights: number) => {
    const updatedCampers = [...editingCampers];
    updatedCampers[index].nights = nights;
    setEditingCampers(updatedCampers);
  };

  const handleRemoveCamper = (index: number) => {
    const updatedCampers = editingCampers.filter((_, i) => i !== index);
    setEditingCampers(updatedCampers);
  };

  const handleAddCamper = () => {
    if (newCamper.firstName.trim() && newCamper.lastName.trim() && newCamper.age) {
      const defaultNights = registration?.campDates?.arrivalDate && registration?.campDates?.departureDate 
        ? calculateNights(registration.campDates.arrivalDate, registration.campDates.departureDate)
        : 0;
      
      const camper = {
        firstName: newCamper.firstName.trim(),
        lastName: newCamper.lastName.trim(),
        age: newCamper.age,
        YouthChildrenCategory: newCamper.youthChildrenCategory,
        nights: newCamper.nights || defaultNights
      };
      
      setEditingCampers([...editingCampers, camper]);
      setNewCamper({ 
        firstName: '', 
        lastName: '', 
        age: AgeCategory.Adult, 
        youthChildrenCategory: undefined,
        nights: 0 
      });
      setShowAddNewCamper(false);
    }
  };

  const handleSelectHistoricalCamper = () => {
    if (selectedHistoricalCamper) {
      const camper = historicalCampers.find(c => c.id === selectedHistoricalCamper);
      if (camper) {
        const defaultNights = registration?.campDates?.arrivalDate && registration?.campDates?.departureDate 
          ? calculateNights(registration.campDates.arrivalDate, registration.campDates.departureDate)
          : 0;
        
        const camperToAdd = {
          firstName: camper.firstName,
          lastName: camper.lastName,
          age: camper.age,
          nights: defaultNights
        };
        
        setEditingCampers([...editingCampers, camperToAdd]);
        setSelectedHistoricalCamper('');
      }
    }
  };

  const handleShowAddNewCamper = () => {
    setShowAddNewCamper(true);
    setSelectedHistoricalCamper('');
  };

  const handleProgramInterestChange = async (camperIndex: number, program: keyof ProgramInterests, checked: boolean) => {
    if (!registration?.attendingCampers) return;

    try {
      const updatedCampers = registration.attendingCampers.map(normalizeCamperForSave);
      const camper = updatedCampers[camperIndex];
      
      // Initialize programInterests if it doesn't exist
      if (!camper.programInterests) {
        camper.programInterests = {
          nurseryProgram: false,
          vbsProgram: false,
          youthGroup: false,
          cruiseProgram: false,
          volunteerInterest: false
        };
      }
      
      camper.programInterests[program] = checked;

      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        attendingCampers: updatedCampers
      });

      onRegistrationUpdate({
        ...registration,
        attendingCampers: updatedCampers
      } as Registration);
    } catch (error) {
      console.error('Error updating program interest:', error);
      alert('Failed to update program interest. Please try again.');
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="card-title mb-0" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
            Attending Campers
          </h6>
          {!isEditingCampers ? (
            <Button variant="primary" size="sm" onClick={handleEditCampersClick}>
              Edit
            </Button>
          ) : (
            <div>
              <Button variant="success" size="sm" onClick={handleSaveCampers} className="me-2">
                Save
              </Button>
              <Button variant="secondary" size="sm" onClick={handleCancelEditCampers}>
                Cancel
              </Button>
            </div>
          )}
        </div>
        
        {!isEditingCampers ? (
          // Display mode
          registration?.attendingCampers && registration.attendingCampers.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>CATEGORY</th>
                    <th>NIGHTS</th>
                    <th className="text-center">NURSERY</th>
                    <th className="text-center">VBS</th>
                    <th className="text-center">YOUTH</th>
                    <th className="text-center">CRUISE</th>
                    <th className="text-center">VOLUNTEER</th>
                  </tr>
                </thead>
                <tbody>
                  {registration.attendingCampers.map((camper: any, index: number) => {
                    const nights = camper.nights || registration?.stayDetails?.numberOfNights || 0;
                    return (
                      <tr key={index}>
                        <td>{`${camper.firstName} ${camper.lastName}`}</td>
                        <td style={{ minWidth: '200px' }}>
                          <select
                            className="form-control form-control-sm"
                            value={normalizeAgeCategory(camper.age)}
                            onChange={(e) => handleCamperCategoryChange(index, e.target.value as AgeCategory)}
                          >
                            <option value={AgeCategory.MustChange}>Must Change</option>
                            <option value={AgeCategory.Adult}>Adult</option>
                            <option value={AgeCategory.AdultStudent}>Adult Student</option>
                            <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                          </select>
                          {normalizeAgeCategory(camper.age) === AgeCategory.YouthChildren && (
                            <select
                              className="form-control form-control-sm mt-1"
                              value={resolveYouthChildrenCategory(camper) || ''}
                              onChange={(e) => handleYouthChildrenSubCategoryChange(index, e.target.value as YouthChildrenSubCategory)}
                            >
                              <option value="">Select Grade Level</option>
                              <option value={YouthChildrenSubCategory.Nursery}>{YouthChildrenSubCategory.Nursery}</option>
                              <option value={YouthChildrenSubCategory.PreSchool}>{YouthChildrenSubCategory.PreSchool}</option>
                              <option value={YouthChildrenSubCategory.GradeP5}>{YouthChildrenSubCategory.GradeP5}</option>
                              <option value={YouthChildrenSubCategory.Grade68}>{YouthChildrenSubCategory.Grade68}</option>
                              <option value={YouthChildrenSubCategory.Grade912}>{YouthChildrenSubCategory.Grade912}</option>
                            </select>
                          )}
                        </td>
                        <td>{getNightsDisplayText(nights)}</td>
                        <td className="text-center align-middle">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`nursery-${index}`}
                            checked={camper.programInterests?.nurseryProgram || false}
                            onChange={(e) => handleProgramInterestChange(index, 'nurseryProgram', e.target.checked)}
                            title="Nursery Program"
                          />
                        </td>
                        <td className="text-center align-middle">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`vbs-${index}`}
                            checked={camper.programInterests?.vbsProgram || false}
                            onChange={(e) => handleProgramInterestChange(index, 'vbsProgram', e.target.checked)}
                            title="VBS Program"
                          />
                        </td>
                        <td className="text-center align-middle">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`youth-${index}`}
                            checked={camper.programInterests?.youthGroup || false}
                            onChange={(e) => handleProgramInterestChange(index, 'youthGroup', e.target.checked)}
                            title="Youth Group"
                          />
                        </td>
                        <td className="text-center align-middle">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`cruise-${index}`}
                            checked={camper.programInterests?.cruiseProgram || false}
                            onChange={(e) => handleProgramInterestChange(index, 'cruiseProgram', e.target.checked)}
                            title="Cruise Program"
                          />
                        </td>
                        <td className="text-center align-middle">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`volunteer-${index}`}
                            checked={camper.programInterests?.volunteerInterest || false}
                            onChange={(e) => handleProgramInterestChange(index, 'volunteerInterest', e.target.checked)}
                            title="Volunteer Interest"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">No campers registered</p>
          )
        ) : (
          // Edit mode
          <div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>CATEGORY</th>
                    <th>NIGHTS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {editingCampers.map((camper: any, index: number) => (
                    <tr key={index}>
                      <td>{`${camper.firstName} ${camper.lastName}`}</td>
                      <td style={{ minWidth: '200px' }}>
                        <select
                          className="form-control form-control-sm"
                          value={normalizeAgeCategory(camper.age)}
                          onChange={(e) => {
                            const selectedAge = e.target.value as AgeCategory;
                            const updatedCampers = [...editingCampers];
                            updatedCampers[index] = {
                              ...updatedCampers[index],
                              age: selectedAge,
                              YouthChildrenCategory: selectedAge === AgeCategory.YouthChildren
                                ? (resolveYouthChildrenCategory(updatedCampers[index]) || YouthChildrenSubCategory.Nursery)
                                : undefined
                            };
                            setEditingCampers(updatedCampers);
                          }}
                        >
                          <option value={AgeCategory.MustChange}>Must Change</option>
                          <option value={AgeCategory.Adult}>Adult</option>
                          <option value={AgeCategory.AdultStudent}>Adult Student</option>
                          <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                        </select>
                        {camper.age === AgeCategory.YouthChildren && (
                          <select
                            className="form-control form-control-sm mt-1"
                            value={resolveYouthChildrenCategory(camper) || ''}
                            onChange={(e) => {
                              const updatedCampers = [...editingCampers];
                              updatedCampers[index] = {
                                ...updatedCampers[index],
                                YouthChildrenCategory: e.target.value as YouthChildrenSubCategory
                              };
                              setEditingCampers(updatedCampers);
                            }}
                          >
                            <option value="">Select Grade Level</option>
                            <option value={YouthChildrenSubCategory.Nursery}>{YouthChildrenSubCategory.Nursery}</option>
                            <option value={YouthChildrenSubCategory.PreSchool}>{YouthChildrenSubCategory.PreSchool}</option>
                            <option value={YouthChildrenSubCategory.GradeP5}>{YouthChildrenSubCategory.GradeP5}</option>
                            <option value={YouthChildrenSubCategory.Grade68}>{YouthChildrenSubCategory.Grade68}</option>
                            <option value={YouthChildrenSubCategory.Grade912}>{YouthChildrenSubCategory.Grade912}</option>
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={camper.nights || 0}
                          onChange={(e) => handleCamperNightsChange(index, parseInt(e.target.value) || 0)}
                          min="0"
                          max="20"
                          style={{ width: '80px' }}
                        />
                      </td>
                      <td>
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleRemoveCamper(index)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Add camper section */}
            <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <h6 className="mb-3">Add Camper</h6>
              
              {!showAddNewCamper ? (
                // Historical campers selection
                <div>
                  {historicalCampers.length > 0 ? (
                    <div className="row">
                      <div className="col-md-8">
                        <select
                          className="form-control form-control-sm"
                          value={selectedHistoricalCamper}
                          onChange={(e) => setSelectedHistoricalCamper(e.target.value)}
                        >
                          <option value="">Select from previous campers...</option>
                          {historicalCampers.map((camper: any) => (
                            <option key={camper.id} value={camper.id}>
                              {camper.firstName} {camper.lastName} (Age: {camper.age})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={handleSelectHistoricalCamper}
                          disabled={!selectedHistoricalCamper}
                          className="me-2"
                        >
                          Add Selected
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          onClick={handleShowAddNewCamper}
                        >
                          Add New Camper
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col-md-8">
                        <p className="text-muted mb-2">No previous campers found for this primary camper.</p>
                      </div>
                      <div className="col-md-4">
                        <Button 
                          variant="primary" 
                          size="sm" 
                          onClick={handleShowAddNewCamper}
                        >
                          Add New Camper
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Add new camper form
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Add New Camper</h6>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setShowAddNewCamper(false)}
                    >
                      Back to Previous Campers
                    </Button>
                  </div>
                  <div className="row">
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="First Name"
                        value={newCamper.firstName}
                        onChange={(e) => setNewCamper({...newCamper, firstName: e.target.value})}
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Last Name"
                        value={newCamper.lastName}
                        onChange={(e) => setNewCamper({...newCamper, lastName: e.target.value})}
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-control form-control-sm"
                        value={newCamper.age}
                        onChange={(e) => setNewCamper({
                          ...newCamper, 
                          age: e.target.value as AgeCategory,
                          youthChildrenCategory: e.target.value === AgeCategory.YouthChildren ? 
                            newCamper.youthChildrenCategory : undefined
                        })}
                      >
                        <option value={AgeCategory.Adult}>Adult</option>
                        <option value={AgeCategory.AdultStudent}>Adult Student</option>
                        <option value={AgeCategory.YouthChildren}>Youth/Children</option>
                      </select>
                    </div>
                    {newCamper.age === AgeCategory.YouthChildren && (
                      <div className="col-md-2">
                        <select
                          className="form-control form-control-sm"
                          value={newCamper.youthChildrenCategory || ''}
                          onChange={(e) => setNewCamper({
                            ...newCamper,
                            youthChildrenCategory: e.target.value as YouthChildrenSubCategory
                          })}
                        >
                          <option value="">Select Grade Level</option>
                          <option value={YouthChildrenSubCategory.Nursery}>{YouthChildrenSubCategory.Nursery}</option>
                          <option value={YouthChildrenSubCategory.PreSchool}>{YouthChildrenSubCategory.PreSchool}</option>
                          <option value={YouthChildrenSubCategory.GradeP5}>{YouthChildrenSubCategory.GradeP5}</option>
                          <option value={YouthChildrenSubCategory.Grade68}>{YouthChildrenSubCategory.Grade68}</option>
                          <option value={YouthChildrenSubCategory.Grade912}>{YouthChildrenSubCategory.Grade912}</option>
                        </select>
                      </div>
                    )}
                    <div className="col-md-2">
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="Nights"
                        value={newCamper.nights || ''}
                        onChange={(e) => setNewCamper({...newCamper, nights: parseInt(e.target.value) || 0})}
                        min="0"
                        max="20"
                      />
                    </div>
                    <div className="col-md-2">
                      <Button 
                        variant="primary" 
                        size="sm" 
                        onClick={handleAddCamper}
                        disabled={
                          !newCamper.firstName.trim() || 
                          !newCamper.lastName.trim() || 
                          !newCamper.age ||
                          (newCamper.age === AgeCategory.YouthChildren && !newCamper.youthChildrenCategory)
                        }
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendingCampersCard;

